import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useContext } from 'react';
import { IUser } from '../../../../extViewmodels';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useLeadServedRoleAutocompleteQuery } from '../../../../queries/LeadServed/useLeadServedRoleAutocompleteQuery';
import { ComboboxContent, ComboboxInput, ComboboxList } from '../../../../web/components-v2/Combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components-v2/Select';
import {
	ManagerCombobox,
	ManagerComboboxStatus,
	UserCombobox,
	UserComboboxAvatarItem,
	UserComboboxStatus,
} from '../../../../web/components/UserCombobox';
import { ILeaderboardFilter } from '../../../queries/Leaderboard/criteria';
import { ViewportContext } from '../../../hooks/useViewport';
import { useQuotaTeamAutocompleteQuery } from '../../../queries/Aida/useQuotaTeamAutocomplete';
import { useTeamAutocompleteQuery } from '../../../queries/Aida/useTeamAutocomplete';
import { useVerticalAutocompleteQuery } from '../../../queries/Aida/useVerticalAutocomplete';
import { styleSheet } from '../styles';
import { OfficeSelect } from './OfficeSelect';
import { SearchFilter, useSyncedSearch } from './SearchFilter';

const ALL_QUOTA_TEAMS = 'all';

interface IFieldProps {
	fieldStyle: StyleDeclarationValue;
}

interface ILeaderboardFilterProps {
	filter: ILeaderboardFilter;
	onChange: (filter: ILeaderboardFilter) => void;
}

export const Filters: React.FC<ILeaderboardFilterProps> = ({ filter, onChange }) => {
	const { isAboveSmall } = useContext(ViewportContext);
	const userSession = useUserSession();
	const isSalesCoffeeAccount = !!userSession?.account?.isLevitateSalesCoffeeAccount;

	const update = (next: Partial<ILeaderboardFilter>) => onChange({ ...filter, ...next });
	const fieldStyle = isSalesCoffeeAccount ? styleSheet.filterField : styleSheet.filterFieldWide;

	return (
		<div className={css(styleSheet.filterPanel)}>
			{isAboveSmall ? (
				<SdrFilter fieldStyle={fieldStyle} user={filter.User} onChange={User => update({ User })} />
			) : null}
			<ManagerFilter fieldStyle={fieldStyle} manager={filter.Manager} onChange={Manager => update({ Manager })} />
			{isAboveSmall ? (
				<>
					<RoleFilter
						fieldStyle={fieldStyle}
						role={filter.UserRole ?? null}
						onChange={UserRole => update({ UserRole })}
					/>
					<TeamFilter fieldStyle={fieldStyle} team={filter.Team ?? null} onChange={Team => update({ Team })} />
					<VerticalFilter
						fieldStyle={fieldStyle}
						vertical={filter.Vertical ?? null}
						onChange={Vertical => update({ Vertical })}
					/>
				</>
			) : null}
			{isAboveSmall && isSalesCoffeeAccount ? (
				<>
					<QuotaTeamFilter
						fieldStyle={fieldStyle}
						quotaTeam={filter.QuotaTeam ?? null}
						onChange={QuotaTeam => update({ QuotaTeam })}
					/>
					<OfficeSelect
						selected={filter.OfficeLocation ?? null}
						onChange={OfficeLocation => update({ OfficeLocation })}
						styles={[fieldStyle]}
					/>
				</>
			) : null}
		</div>
	);
};

const SdrFilter: React.FC<IFieldProps & { user?: IUser; onChange: (user: IUser | undefined) => void }> = ({
	fieldStyle,
	user,
	onChange,
}) => (
	<div className={css(fieldStyle)}>
		<UserCombobox
			id={user?.id}
			key={`sdr-${user?.id ?? 'any'}`}
			onValueChange={(next: IUser | null) => onChange(next ?? undefined)}
		>
			<ComboboxInput aria-label='SDR' placeholder='Filter by SDR' showClear />
			<ComboboxContent>
				<UserComboboxStatus />
				<ComboboxList>{(item: IUser) => <UserComboboxAvatarItem key={item.id} user={item} />}</ComboboxList>
			</ComboboxContent>
		</UserCombobox>
	</div>
);

const ManagerFilter: React.FC<IFieldProps & { manager?: IUser; onChange: (manager: IUser | undefined) => void }> = ({
	fieldStyle,
	manager,
	onChange,
}) => (
	<div className={css(fieldStyle)}>
		<ManagerCombobox
			id={manager?.id}
			key={`manager-${manager?.id ?? 'any'}`}
			onValueChange={(next: IUser | null) => onChange(next ?? undefined)}
		>
			<ComboboxInput aria-label='Manager' placeholder='Filter by Manager' showClear />
			<ComboboxContent>
				<ManagerComboboxStatus />
				<ComboboxList>{(item: IUser) => <UserComboboxAvatarItem key={item.id} user={item} />}</ComboboxList>
			</ComboboxContent>
		</ManagerCombobox>
	</div>
);

const RoleFilter: React.FC<IFieldProps & { role: string | null; onChange: (role: string | undefined) => void }> = ({
	fieldStyle,
	role,
	onChange,
}) => {
	const [search, setSearch] = useSyncedSearch(role);
	const { data: roles = [] } = useLeadServedRoleAutocompleteQuery({ fragment: search });
	return (
		<SearchFilter
			ariaLabel='Role'
			emptyText='No roles found.'
			onChange={next => onChange(next ?? undefined)}
			onSearchChange={setSearch}
			options={roles}
			placeholder='Filter by Role'
			search={search}
			styles={[fieldStyle]}
			value={role}
		/>
	);
};

const TeamFilter: React.FC<IFieldProps & { team: string | null; onChange: (team: string | undefined) => void }> = ({
	fieldStyle,
	team,
	onChange,
}) => {
	const [search, setSearch] = useSyncedSearch(team);
	const { data: teams = [] } = useTeamAutocompleteQuery({ fragment: search });
	return (
		<SearchFilter
			ariaLabel='Team'
			emptyText='No teams found.'
			onChange={next => onChange(next ?? undefined)}
			onSearchChange={setSearch}
			options={teams}
			placeholder='Filter by Team'
			search={search}
			styles={[fieldStyle]}
			value={team}
		/>
	);
};

const VerticalFilter: React.FC<
	IFieldProps & { vertical: string | null; onChange: (vertical: string | undefined) => void }
> = ({ fieldStyle, vertical, onChange }) => {
	const [search, setSearch] = useSyncedSearch(vertical);
	const { data: verticals = [] } = useVerticalAutocompleteQuery({ fragment: search });
	return (
		<SearchFilter
			ariaLabel='Vertical'
			emptyText='No verticals found.'
			onChange={next => onChange(next ?? undefined)}
			onSearchChange={setSearch}
			options={verticals}
			placeholder='Filter by Vertical'
			search={search}
			styles={[fieldStyle]}
			value={vertical}
		/>
	);
};

const QuotaTeamFilter: React.FC<
	IFieldProps & { quotaTeam: string | null; onChange: (quotaTeam: string | undefined) => void }
> = ({ fieldStyle, quotaTeam, onChange }) => {
	const { data: quotaTeams = [], isPending, isError } = useQuotaTeamAutocompleteQuery();
	const isEmpty = !isPending && !isError && quotaTeams.length === 0;
	const placeholder = isPending
		? 'Loading quota teams...'
		: isError
			? 'Error loading quota teams'
			: isEmpty
				? 'No quota teams configured'
				: 'Filter by Quota Team';
	const items = [
		{ label: 'All quota teams', value: ALL_QUOTA_TEAMS },
		...quotaTeams.map(team => ({ label: `Quota team: ${team}`, value: team })),
	];

	return (
		<div className={css(fieldStyle)}>
			<Select
				items={items}
				value={quotaTeam}
				disabled={isPending || isEmpty}
				onValueChange={(next: string | null) => onChange(!next || next === ALL_QUOTA_TEAMS ? undefined : next)}
			>
				<SelectTrigger aria-label='Quota team' styles={[styleSheet.selectTrigger]}>
					<SelectValue placeholder={placeholder} />
				</SelectTrigger>
				<SelectContent>
					{items.map(item => (
						<SelectItem key={item.value} value={item.value}>
							{item.value === ALL_QUOTA_TEAMS ? item.label : item.value}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	);
};
